import React, { useState, useEffect } from "react";
import { ExternalLink, ArrowRight } from "lucide-react";
import { Link } from "react-router-dom";
import { Image } from "@/components/ui/image";
import { base44 } from "@/api/base44Client";
import { transformConcept } from "@/lib/conceptUtils";
import { useReveal } from "@/hooks/useReveal";

export default function Portfolio() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const gridRef = useReveal(!loading && items.length > 0);

  useEffect(() => {
    base44.entities.Concept.filter(
      { item_type: "portfolio", published: true },
      "sort_order"
    )
      .then((records) => setItems(records.map(transformConcept)))
      .catch(() => setItems([]))
      .finally(() => setLoading(false));
  }, []);

  if (!loading && items.length === 0) return null;

  return (
    <section id="portfolio" className="py-16 sm:py-32">
      <div className="max-w-7xl mx-auto px-5 sm:px-8">
        <div className="max-w-2xl mb-14">
          <span className="text-xs font-semibold tracking-[0.2em] uppercase text-amber-700">
            Our Work
          </span>
          <h2 className="mt-4 font-heading font-extrabold text-4xl sm:text-5xl text-foreground text-balance">
            Real websites we've built for real businesses.
          </h2>
          <p className="mt-5 text-lg text-foreground/70">
            A selection of live client projects — launched, hosted, and
            maintained by Bluegrass Web Works.
          </p>
        </div>

        {loading ? (
          <div className="flex justify-center py-20">
            <div className="w-8 h-8 border-4 border-border border-t-accent rounded-full animate-spin" />
          </div>
        ) : (
          <div ref={gridRef} className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {items.map((item) => (
              <div
                key={item.slug}
                className="group reveal flex flex-col rounded-[1.5rem] bg-card border border-border/70 overflow-hidden hover:border-accent/40 hover:shadow-lg hover:shadow-accent/5 transition-all duration-300"
              >
                <div className="relative h-48 overflow-hidden">
                  {item.heroImage && (
                    <Image
                      src={item.heroImage}
                      alt={`Homepage screenshot of the ${item.title} website built by Bluegrass Web Works`}
                      fittingType="fill"
                      className="w-full h-full transition-transform duration-500 group-hover:scale-105"
                    />
                  )}
                  <div
                    className="absolute inset-0"
                    style={{
                      background: `linear-gradient(to top, ${item.accent}cc, transparent)`,
                    }}
                  />
                  <div className="absolute bottom-0 left-0 right-0 p-4 text-white">
                    <h3 className="font-heading font-bold text-xl">{item.title}</h3>
                    {item.name && (
                      <p className="text-sm text-white/80">{item.name}</p>
                    )}
                  </div>
                </div>
                <div className="px-6 py-5 flex flex-col flex-1">
                  <p className="text-sm text-foreground/70 leading-relaxed">
                    {item.summary}
                  </p>
                  {item.features && item.features.length > 0 && (
                    <ul className="mt-4 flex flex-wrap gap-1.5">
                      {item.features.slice(0, 4).map((f) => (
                        <li
                          key={f}
                          className="text-xs font-medium px-2.5 py-1 rounded-full bg-secondary/70 text-accent"
                        >
                          {f}
                        </li>
                      ))}
                    </ul>
                  )}
                  <div className="mt-auto pt-5 flex items-center justify-between gap-3">
                    <Link
                      to={`/work/${item.slug}`}
                      className="inline-flex items-center gap-1.5 text-sm font-semibold text-accent group-hover:gap-2.5 transition-all"
                    >
                      Read case study <ArrowRight size={15} />
                    </Link>
                    {item.clientUrl && (
                      <a
                        href={item.clientUrl}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="inline-flex items-center gap-1.5 text-sm font-medium text-foreground/70 hover:text-accent"
                      >
                        Live site <ExternalLink size={14} />
                      </a>
                    )}
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </section>
  );
}